/**
 * useStaffList — TanStack Query hook over `GET /admin/staff`.
 *
 * The platform staff roster (root + support access) powers the
 * `/staff` list page in the admin portal (SCRUM-131-FE). Response
 * envelope: `{ members: PlatformStaffMemberResponseDto[] }`.
 *
 * Status surface (closed discriminated union):
 *   - 'loading'   — request in flight.
 *   - 'ready'     — 200 OK; `members` populated (projected view-models).
 *   - 'forbidden' — 403; the caller is not platform staff. Distinct
 *                   branch so the page renders a permission-denied
 *                   panel rather than the generic Retry affordance.
 *   - 'error'     — anything else (5xx, network, schema drift).
 *
 * Query key: `['admin','staff']` — the canonical list key that every
 * staff mutation invalidates.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { isPlatformRole } from '../../../shared/auth/admin-permissions.js';
import type { StaffMember } from './types.js';

/** Canonical staff-list query key. Mutations invalidate this. */
export const STAFF_LIST_QUERY_KEY = ['admin', 'staff'] as const;

export type UseStaffListResult =
  | { readonly status: 'loading'; readonly refetch: () => void }
  | {
      readonly status: 'ready';
      readonly members: readonly StaffMember[];
      readonly refetch: () => void;
    }
  | { readonly status: 'forbidden'; readonly refetch: () => void }
  | { readonly status: 'error'; readonly refetch: () => void };

/**
 * Sentinel distinguishing a 403 from any other error inside the
 * queryFn — we cannot `throw` and rely on TanStack's error path
 * because the page needs a permission-denied branch that is NOT the
 * retry/error affordance.
 */
const FORBIDDEN = Symbol('staff-list:forbidden');

interface StaffEnvelope {
  readonly members: readonly StaffMemberDto[];
}

/**
 * Wire shape. The contract types `name` as a nullable string
 * ("Full name, or null when not set"), so we mirror it here and let
 * `normalizeStaffName` derive a UI-safe value at the mapper boundary.
 */
interface StaffMemberDto {
  readonly id: string;
  readonly email: string;
  readonly name: string | null;
  readonly role: string;
  readonly status: 'active' | 'suspended';
}

/**
 * Normalize the wire `name` into a UI-safe non-empty string.
 *
 * The contract types `name` as `string | null` ("Full name, or null when
 * not set"). The normalizer guarantees a non-empty value for the UI:
 *   - non-empty string → trimmed value;
 *   - null or empty/whitespace string → the email local-part (before `@`),
 *     or `'Unnamed'` if that is also empty.
 *
 * Exported for direct unit testing of the boundary.
 */
export function normalizeStaffName(name: string | null, email: string): string {
  if (typeof name === 'string') {
    const trimmed = name.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  const localPart = email.split('@')[0]?.trim() ?? '';
  return localPart.length > 0 ? localPart : 'Unnamed';
}

/** Project a wire DTO into the UI view-model, normalizing `name` + `role`. */
export function projectStaffMember(dto: StaffMemberDto): StaffMember {
  return {
    id: dto.id,
    email: dto.email,
    name: normalizeStaffName(dto.name, dto.email),
    // `role` is `'root' | 'support'` per the contract; guard at runtime
    // and fall back to 'support' (least-privileged) on drift.
    role: isPlatformRole(dto.role) ? dto.role : 'support',
    status: dto.status,
  };
}

export function useStaffList(): UseStaffListResult {
  const query = useQuery({
    queryKey: STAFF_LIST_QUERY_KEY,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET('/admin/staff');
      if (result.response.status === 403) {
        return FORBIDDEN;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /admin/staff failed with status ${result.response.status}`,
        );
      }
      return result.data as StaffEnvelope;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }
  if (query.data === FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }
  return {
    status: 'ready',
    members: query.data.members.map(projectStaffMember),
    refetch,
  };
}
